import React, { useRef, useState } from 'react'
import { type LexicalEditor } from 'lexical'
import { $generateHtmlFromNodes } from '@lexical/html'

import { Modal } from '../../../../components/Modal/Modal'

import { IntegrationServiceId } from '../../../../common/integrations'
import { type CrmActionModalProps } from '../crmActionRegistry'

import { useResolvedResults } from './useResolvedResults'
import { useCrmEligibility } from './useCrmEligibility'
import { useCrmActionSave } from './useCrmActionSave'
import { CrmEligibilityNotice, CrmLoadingBody, CrmNoEligibleAlert } from './CrmEligibilityFeedback'
import { CrmRichTextEditor } from './CrmRichTextEditor'

export const RedtailNoteActionModal: React.FC<CrmActionModalProps> = ({ open, onClose, resolveResults, tracking }) => {
  const { results, resolving } = useResolvedResults(resolveResults)
  const eligibility = useCrmEligibility(IntegrationServiceId.REDTAIL, results ?? [])

  const { save, saving } = useCrmActionSave({
    tracking,
    eligibility,
    onClose,
    serviceId: IntegrationServiceId.REDTAIL,
    sourceObject: 'note',
    entityType: 'notes',
    relatedToType: 'contact',
    successToast: { title: 'Adding note', message: 'Redtail note creation started.' },
    errorToast: { title: 'Failed to add note', message: 'Could not start the Redtail note creation. Please try again.' },
  })

  const editorRef = useRef<LexicalEditor>(null)
  const [textContent, setTextContent] = useState('')

  const count = results?.length ?? 0
  const targetLabel = `${count} ${count === 1 ? 'client' : 'clients'}`

  const noEligibleClients = !resolving && eligibility.status === 'ready' && eligibility.eligibleCount === 0

  const body =
    resolving || eligibility.status === 'loading' ? (
      <CrmLoadingBody crmName='Redtail' />
    ) : noEligibleClients ? (
      <CrmNoEligibleAlert crmName='Redtail' />
    ) : (
      <div className='d-flex flex-column gap-4'>
        <div className='paragraph-medium'>This will run in the background. Feel free to close this window or continue with other tasks.</div>
        <CrmRichTextEditor ref={editorRef} variant='note' onTextContentChange={setTextContent} placeholder={<>Enter a note for selected clients…</>} />
        <CrmEligibilityNotice eligibility={eligibility} verbPhrase='add a note to' crmName='Redtail' />
      </div>
    )

  const handleSave = async () => {
    const editor = editorRef.current
    if (!editor) {
      return
    }
    const html = editor.getEditorState().read(() => $generateHtmlFromNodes(editor, null))
    await save({ note: html })
  }

  const primaryActionDisabled = noEligibleClients
    ? false
    : resolving || eligibility.status !== 'ready' || eligibility.eligibleCount === 0 || textContent.trim().length === 0

  return (
    <Modal
      show={open}
      onHide={onClose}
      header={<h4>Add note to {targetLabel}</h4>}
      body={body}
      headerBodySeparator={true}
      showFooterActions={true}
      footerClassName='px-4 pb-4 pt-0'
      onFooterPrimaryAction={noEligibleClients ? onClose : handleSave}
      footerPrimaryActionTitle={noEligibleClients ? 'Done' : 'Add note'}
      footerPrimaryActionDisabled={primaryActionDisabled}
      footerPrimaryActionLoading={saving}
      onFooterSecondaryAction={onClose}
      hideSecondaryAction={noEligibleClients}
      centered={true}
    />
  )
}
